  import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, ArrowRight, AlertCircle } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import usePlatformConfig from "@/lib/usePlatformConfig";

export default function SubscribeSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const t = useT("saas");
  const { trialPeriodDays: days } = usePlatformConfig();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, status: null, tenantSlug: null, tenantName: null, tenantStatus: null });
  const [attempts, setAttempts] = useState(0);

  // Un seul rail d'abonnement plateforme : Stripe (Square retiré le 2026-08-04, avec sa
  // sonde `/saas/square/confirm`). Le sondage reste la voie de finalisation vue par
  // l'acheteur ; le webhook stripe-saas est la voie indépendante du navigateur.
  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, status: "error" });
      return;
    }

    const pollStripe = async (n = 0) => {
      try {
        const { data } = await api.get(`/saas/subscribe/status/${sessionId}`);
        setState({
          loading: false,
          status: data.status,
          tenantSlug: data.tenant_slug,
          // Le NOM du cabinet, pas son adresse : un cabinet nommé "vamsi" à l'adresse
          // "vamsiiii" était accueilli comme "vamsiiii" sur le seul écran que voit un
          // nouveau client. Repli sur le slug si le nom manque.
          tenantName: data.tenant_name || data.tenant_slug,
          tenantStatus: data.tenant_status,
          demoMode: data.demo_mode,
        });
        if (data.status === "completed") return;
        if (n >= 8) return;
        setAttempts(n + 1);
        setTimeout(() => pollStripe(n + 1), 2000);
      } catch (e) {
        setState({ loading: false, status: "error" });
      }
    };

    pollStripe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const goToDashboard = () => {
    if (state.tenantSlug) {
      window.location.href = `/t/${state.tenantSlug}/admin/onboarding`;
    } else {
      navigate("/platform");
    }
  };

  return (
    <div className="min-h-screen bg-sol-midnight text-white flex items-center justify-center p-6">
      <div className="max-w-lg w-full text-center">
        {state.loading && (
          <>
            <Loader2 className="w-12 h-12 text-sol-gold animate-spin mx-auto mb-8" />
            <h1 className="font-sol font-semibold text-3xl mb-4">{t("subscribe.activating")}</h1>
            <p className="text-white/60">
              {t("subscribe.checkingPayment")}{attempts > 0 ? t("subscribe.attemptSuffix", { n: attempts }) : ""}.
            </p>
          </>
        )}

        {!state.loading && state.status === "completed" && (
          <>
            <div className="w-20 h-20 rounded-full bg-sol-gold/20 flex items-center justify-center mx-auto mb-8">
              <CheckCircle2 className="w-12 h-12 text-sol-gold" />
            </div>
            <h1 className="font-sol font-semibold text-4xl mb-4">{t("subscribe.welcomeTitle")}</h1>
            <p className="text-white/70 mb-8 leading-relaxed">
              {t("subscribe.activatedPrefix")} <strong className="text-white">{state.tenantName || state.tenantSlug}</strong> {t("subscribe.activatedSuffix")}
              {state.demoMode && <span className="block mt-2 text-sol-gold text-sm">{t("subscribe.demoModeNote")}</span>}
              {/* `days == null` until `GET /saas/config` resolves — the digit-free generic
                  sentence covers first paint instead of interpolating "undefined-day trial". */}
              <span className="block mt-2 text-sol-gold text-sm">
                {days != null ? t("subscribe.trialStarted", { days }) : t("pricing.subtitle")}
              </span>
            </p>
            <Button
              data-testid="goto-dashboard"
              onClick={goToDashboard}
              className="bg-sol-gold hover:bg-sol-gold/90 text-black rounded-full px-8 py-6"
            >
              {t("subscribe.completeProfile")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}

        {!state.loading && state.status !== "completed" && (
          <>
            <AlertCircle className="w-12 h-12 text-rose-400 mx-auto mb-8" />
            <h1 className="font-sol font-semibold text-3xl mb-4">{t("subscribe.paymentPending")}</h1>
            <p className="text-white/60 mb-8">
              {t("subscribe.pendingHint")}
            </p>
            <Button onClick={() => navigate("/platform/signup")} variant="outline" className="border-white/20 rounded-full">
              {t("subscribe.retry")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
